import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

/**
 * PremiumCard - High-end card component with responsive sizing,
 * visual hierarchy, and sophisticated glassmorphism
 */
const PremiumCard = forwardRef(({
  children,
  variant = 'default',
  size = 'md',
  prominence = 'standard',
  interactive = false,
  glow = false,
  gradient = null,
  className = '',
  header,
  footer,
  ...props
}, ref) => {

  // Size-based padding scale (responsive)
  const sizeStyles = {
    xs: 'p-2 sm:p-3',
    sm: 'p-3 sm:p-4',
    md: 'p-4 sm:p-5 lg:p-6',
    lg: 'p-5 sm:p-6 lg:p-8',
    xl: 'p-6 sm:p-8 lg:p-10',
    flush: 'p-0',
  };

  // Visual prominence levels
  const prominenceStyles = {
    subtle: 'bg-white/[0.02] border-white/[0.05]',
    standard: 'bg-white/[0.04] border-white/[0.08]',
    elevated: 'bg-white/[0.06] border-white/[0.12] shadow-glass',
    featured: 'bg-white/[0.08] border-white/[0.15] shadow-glass-lg',
    hero: 'bg-gradient-to-br from-white/[0.1] to-white/[0.03] border-white/[0.2] shadow-glass-xl',
  };

  // Variant styles
  const variants = {
    default: 'backdrop-blur-sm',
    glass: 'backdrop-blur-md',
    solid: 'bg-surface backdrop-blur-none',
    outline: 'bg-transparent border-2',
    ghost: 'bg-transparent border-transparent',
  };

  // Interactive states
  const interactiveStyles = interactive
    ? `cursor-pointer transition-all duration-300 ease-out-expo
       hover:bg-white/[0.08] hover:border-white/20 hover:shadow-glass-lg
       hover:-translate-y-0.5 active:translate-y-0 active:shadow-glass`
    : '';

  // Glow effect
  const glowStyles = glow
    ? 'shadow-glow-primary hover:shadow-glow-lg'
    : '';

  // Gradient overlays
  const gradientStyles = {
    purple: 'before:absolute before:inset-0 before:bg-gradient-to-br before:from-primary-500/10 before:to-transparent before:rounded-xl before:pointer-events-none',
    blue: 'before:absolute before:inset-0 before:bg-gradient-to-br before:from-secondary-500/10 before:to-transparent before:rounded-xl before:pointer-events-none',
    green: 'before:absolute before:inset-0 before:bg-gradient-to-br before:from-success-500/10 before:to-transparent before:rounded-xl before:pointer-events-none',
    sunset: 'before:absolute before:inset-0 before:bg-gradient-to-br before:from-orange-500/10 before:via-accent-500/5 before:to-transparent before:rounded-xl before:pointer-events-none',
  };

  // Border radius based on size
  const radiusStyles = {
    xs: 'rounded-md',
    sm: 'rounded-lg',
    md: 'rounded-xl',
    lg: 'rounded-2xl',
    xl: 'rounded-3xl',
    flush: 'rounded-xl',
  };

  const cardClasses = `
    relative overflow-hidden border transition-all duration-200
    ${radiusStyles[size]}
    ${variants[variant]}
    ${prominenceStyles[prominence]}
    ${interactiveStyles}
    ${glowStyles}
    ${gradient ? gradientStyles[gradient] : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const contentClasses = sizeStyles[size];

  return (
    <div ref={ref} className={cardClasses} {...props}>
      {header && (
        <PremiumCardHeader size={size}>{header}</PremiumCardHeader>
      )}
      <div className={contentClasses}>
        {children}
      </div>
      {footer && (
        <PremiumCardFooter size={size}>{footer}</PremiumCardFooter>
      )}
    </div>
  );
});

PremiumCard.displayName = 'PremiumCard';

PremiumCard.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'glass', 'solid', 'outline', 'ghost']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'flush']),
  prominence: PropTypes.oneOf(['subtle', 'standard', 'elevated', 'featured', 'hero']),
  interactive: PropTypes.bool,
  glow: PropTypes.bool,
  gradient: PropTypes.oneOf([null, 'purple', 'blue', 'green', 'sunset']),
  className: PropTypes.string,
  header: PropTypes.node,
  footer: PropTypes.node,
};

/**
 * PremiumCardHeader with size-responsive padding
 */
export const PremiumCardHeader = ({ children, size = 'md', className = '' }) => {
  const paddingStyles = {
    xs: 'px-2 py-1.5 sm:px-3 sm:py-2',
    sm: 'px-3 py-2 sm:px-4 sm:py-2.5',
    md: 'px-4 py-3 sm:px-5 sm:py-3.5 lg:px-6 lg:py-4',
    lg: 'px-5 py-3.5 sm:px-6 sm:py-4 lg:px-8 lg:py-5',
    xl: 'px-6 py-4 sm:px-8 sm:py-5 lg:px-10 lg:py-6',
    flush: 'px-4 py-3',
  };

  return (
    <div className={`border-b border-white/[0.06] ${paddingStyles[size]} ${className}`}>
      {children}
    </div>
  );
};

/**
 * PremiumCardFooter with size-responsive padding
 */
export const PremiumCardFooter = ({ children, size = 'md', className = '' }) => {
  const paddingStyles = {
    xs: 'px-2 py-1.5 sm:px-3 sm:py-2',
    sm: 'px-3 py-2 sm:px-4 sm:py-2.5',
    md: 'px-4 py-3 sm:px-5 sm:py-3.5 lg:px-6 lg:py-4',
    lg: 'px-5 py-3.5 sm:px-6 sm:py-4 lg:px-8 lg:py-5',
    xl: 'px-6 py-4 sm:px-8 sm:py-5 lg:px-10 lg:py-6',
    flush: 'px-4 py-3',
  };

  return (
    <div className={`border-t border-white/[0.06] bg-black/10 ${paddingStyles[size]} ${className}`}>
      {children}
    </div>
  );
};

/**
 * Stat display for important metrics
 */
export const PremiumStat = ({
  value,
  label,
  trend,
  trendDirection = 'up',
  size = 'md',
  color = 'default',
  className = '',
}) => {
  const sizeStyles = {
    xs: { value: 'text-lg font-semibold', label: 'text-[10px]', trend: 'text-[10px]' },
    sm: { value: 'text-xl font-bold', label: 'text-xs', trend: 'text-xs' },
    md: { value: 'text-2xl sm:text-3xl font-bold', label: 'text-xs sm:text-sm', trend: 'text-xs' },
    lg: { value: 'text-3xl sm:text-4xl font-bold', label: 'text-sm', trend: 'text-sm' },
    xl: { value: 'text-4xl sm:text-5xl font-bold tracking-tight', label: 'text-base', trend: 'text-base' },
  };

  const colorStyles = {
    default: 'text-white',
    primary: 'text-primary-400',
    secondary: 'text-secondary-400',
    success: 'text-success-400',
    warning: 'text-warning-400',
    error: 'text-error-400',
  };

  const trendColors = {
    up: 'text-success-400',
    down: 'text-error-400',
    neutral: 'text-gray-400',
  };

  return (
    <div className={`text-center ${className}`}>
      <div className={`${sizeStyles[size].value} ${colorStyles[color]} tabular-nums`}>
        {value}
      </div>
      {trend && (
        <div className={`${sizeStyles[size].trend} ${trendColors[trendDirection]} mt-1 flex items-center justify-center gap-0.5`}>
          {trendDirection === 'up' && '↑'}
          {trendDirection === 'down' && '↓'}
          {trend}
        </div>
      )}
      <div className={`${sizeStyles[size].label} text-gray-400 mt-1 uppercase tracking-wider`}>
        {label}
      </div>
    </div>
  );
};

/**
 * Section divider with optional label
 */
export const PremiumDivider = ({ label, className = '' }) => {
  if (!label) {
    return <hr className={`border-t border-white/[0.06] my-4 ${className}`} />;
  }

  return (
    <div className={`flex items-center gap-3 my-4 ${className}`}>
      <hr className="flex-1 border-t border-white/[0.06]" />
      <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
      <hr className="flex-1 border-t border-white/[0.06]" />
    </div>
  );
};

export default PremiumCard;
